// Sync engine: flushes the outbox to the API. No browser / auth imports here — everything is injected,
// so the engine is unit-testable (see `test/offline.test.ts`); the app wiring lives in `runtime.ts`.
import { ATTENDANCE_CHUNK_SIZE, backoffDelayMs, buildSummaryCalls, chunk, classifySyncError } from './core';
import type { Outbox } from './outbox';
import type {
  AttendanceOutboxEntry,
  ClassifiedSyncError,
  OutboxEntry,
  SessionSummaryOutboxEntry,
  SyncRequestContext,
  SyncTransport,
} from './types';

export interface SyncEngineDeps {
  outbox: Outbox;
  transport: SyncTransport;
  /** Current signed-in user (`sub`); only their entries are flushed. `null` = not signed in → nothing is sent. */
  getCurrentUserId: () => string | null | Promise<string | null>;
  /** Silent token renew (`userManager.signinSilent()`); resolves true when a new token is available. */
  renewAuth: () => Promise<boolean>;
  /** Called after a flush that resolved (removed or rejected) entries, with the affected school ids. */
  onSynced?: (schoolIds: string[]) => void;
  /** Called when a request reached / did not reach the server (feeds `useOnlineStatus`). */
  onNetworkStatus?: (reachable: boolean) => void;
  onFlushStateChange?: (flushing: boolean) => void;
  isOnline?: () => boolean;
  now?: () => number;
}

export interface FlushOptions {
  /** Ignore per-entry backoff (manual retry, `online` event, app start, after login). */
  force?: boolean;
}

export interface FlushSummary {
  sent: number;
  applied: number;
  rejected: number;
  failed: number;
  authFailed: boolean;
  networkFailed: boolean;
}

const emptySummary = (): FlushSummary => ({ sent: 0, applied: 0, rejected: 0, failed: 0, authFailed: false, networkFailed: false });

export class SyncEngine {
  private running: Promise<FlushSummary> | null = null;
  private rerunRequested = false;
  private rerunForce = false;
  private readonly now: () => number;
  private readonly deps: SyncEngineDeps;

  constructor(deps: SyncEngineDeps) {
    this.deps = deps;
    this.now = deps.now ?? (() => Date.now());
  }

  get isFlushing(): boolean {
    return this.running !== null;
  }

  /**
   * Single-flight flush. A call made while a flush is running schedules exactly one more pass after it and
   * returns the promise of the running pass chain.
   */
  flush(options: FlushOptions = {}): Promise<FlushSummary> {
    if (this.running) {
      this.rerunRequested = true;
      this.rerunForce = this.rerunForce || !!options.force;
      return this.running;
    }
    this.deps.onFlushStateChange?.(true);
    this.running = (async () => {
      let total = emptySummary();
      let force = !!options.force;
      let renewedOnce = false;
      for (;;) {
        this.rerunRequested = false;
        this.rerunForce = false;
        const pass = await this.flushOnce(force);
        total = mergeSummaries(total, pass);
        let again = this.rerunRequested;
        force = this.rerunForce;
        if (pass.authFailed && !renewedOnce) {
          renewedOnce = true;
          const renewed = await this.deps.renewAuth().catch(() => false);
          if (renewed) {
            again = true;
            force = true;
          }
        }
        if (!again) break;
      }
      return total;
    })().finally(() => {
      this.running = null;
      this.deps.onFlushStateChange?.(false);
    });
    return this.running;
  }

  private async flushOnce(force: boolean): Promise<FlushSummary> {
    const summary = emptySummary();
    if (this.deps.isOnline && !this.deps.isOnline()) return summary;
    const userId = await this.deps.getCurrentUserId();
    if (!userId) return summary;

    const nowMs = this.now();
    const due = this.deps.outbox
      .forUser(userId)
      .filter((e) => e.state === 'pending' && (force || !e.nextAttemptAt || e.nextAttemptAt <= nowMs));
    if (due.length === 0) return summary;

    const touchedSchools = new Set<string>();
    // Sessions whose attendance did not go through in this pass — their "complete" summary waits for it.
    const blockedSessions = new Set<string>();
    let stop = false;

    const attendance = due.filter((e): e is AttendanceOutboxEntry => e.kind === 'attendance');
    for (const group of groupBy(attendance, (e) => `${e.schoolId}|${e.activeRole ?? ''}`)) {
      if (stop) break;
      const ctx: SyncRequestContext = { schoolId: group[0].schoolId, activeRole: group[0].activeRole };
      for (const part of chunk(group, ATTENDANCE_CHUNK_SIZE)) {
        if (stop) {
          part.forEach((e) => blockedSessions.add(e.sessionId));
          continue;
        }
        summary.sent += part.length;
        try {
          const results = await this.deps.transport.bulkMarkAttendance(
            ctx,
            part.map((e) => ({ enrollmentId: e.enrollmentId, status: e.status, markedAt: e.markedAt }))
          );
          this.deps.onNetworkStatus?.(true);
          const byEnrollment = new Map((results ?? []).map((r) => [r.enrollmentId, r]));
          for (const entry of part) {
            const result = byEnrollment.get(entry.enrollmentId);
            if (!result) {
              this.markFailed(entry, { kind: 'transient', code: 'missing_result' }, summary);
              blockedSessions.add(entry.sessionId);
            } else if (result.outcome === 'Applied' || result.outcome === 'Stale') {
              this.deps.outbox.removeIfUnchanged(entry);
              summary.applied++;
              touchedSchools.add(entry.schoolId);
            } else {
              this.markRejected(entry, result.errorCode ?? 'rejected', summary);
              touchedSchools.add(entry.schoolId);
            }
          }
        } catch (error) {
          const classified = classifySyncError(error);
          stop = this.handleRequestError(part, classified, summary, touchedSchools);
          if (classified.kind !== 'rejected') part.forEach((e) => blockedSessions.add(e.sessionId));
        }
      }
    }

    const summaries = due.filter((e): e is SessionSummaryOutboxEntry => e.kind === 'sessionSummary');
    for (const entry of summaries) {
      if (stop) break;
      if (entry.complete && blockedSessions.has(entry.sessionId)) continue;
      const ctx: SyncRequestContext = { schoolId: entry.schoolId, activeRole: entry.activeRole };
      summary.sent++;
      try {
        for (const call of buildSummaryCalls(entry)) {
          if (call.type === 'summarize') {
            await this.deps.transport.summarize(ctx, entry.sessionId, {
              notes: call.body.notes ?? '',
              topic: call.body.topic ?? '',
              title: call.body.title,
              clientUpdatedAt: call.body.clientUpdatedAt,
            });
          } else if (call.type === 'notes') {
            await this.deps.transport.updateNotes(ctx, entry.sessionId, {
              notes: call.body.notes ?? '',
              clientUpdatedAt: call.body.clientUpdatedAt,
            });
          } else {
            await this.deps.transport.updateTopic(ctx, entry.sessionId, {
              topic: call.body.topic ?? '',
              clientUpdatedAt: call.body.clientUpdatedAt,
            });
          }
        }
        this.deps.onNetworkStatus?.(true);
        this.deps.outbox.removeIfUnchanged(entry);
        summary.applied++;
        touchedSchools.add(entry.schoolId);
      } catch (error) {
        stop = this.handleRequestError([entry], classifySyncError(error), summary, touchedSchools);
      }
    }

    if (touchedSchools.size > 0) this.deps.onSynced?.([...touchedSchools]);
    return summary;
  }

  /** Applies a whole-request failure to its entries. Returns true when the flush should stop. */
  private handleRequestError(
    entries: OutboxEntry[],
    error: ClassifiedSyncError,
    summary: FlushSummary,
    touchedSchools: Set<string>
  ): boolean {
    if (error.kind === 'rejected') {
      this.deps.onNetworkStatus?.(true);
      for (const entry of entries) {
        this.markRejected(entry, error.code, summary, error.message);
        touchedSchools.add(entry.schoolId);
      }
      return false;
    }
    for (const entry of entries) this.markFailed(entry, error, summary);
    if (error.kind === 'network') {
      this.deps.onNetworkStatus?.(false);
      summary.networkFailed = true;
      return true;
    }
    this.deps.onNetworkStatus?.(true);
    if (error.kind === 'auth') {
      summary.authFailed = true;
      return true;
    }
    return false;
  }

  private markRejected(entry: OutboxEntry, code: string, summary: FlushSummary, message?: string) {
    this.deps.outbox.patchIfUnchanged(entry, {
      state: 'rejected',
      errorCode: code,
      lastError: message,
      nextAttemptAt: undefined,
    });
    summary.rejected++;
  }

  /** Never drops the entry: keeps it pending with exponential backoff (max 5 min). */
  private markFailed(entry: OutboxEntry, error: ClassifiedSyncError, summary: FlushSummary) {
    const attempts = entry.attempts + 1;
    this.deps.outbox.patchIfUnchanged(entry, {
      attempts,
      nextAttemptAt: this.now() + backoffDelayMs(attempts),
      lastError: error.message ? `${error.code}: ${error.message}` : error.code,
    });
    summary.failed++;
  }
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): T[][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = keyOf(item);
    const list = groups.get(k);
    if (list) list.push(item);
    else groups.set(k, [item]);
  }
  return [...groups.values()];
}

function mergeSummaries(a: FlushSummary, b: FlushSummary): FlushSummary {
  return {
    sent: a.sent + b.sent,
    applied: a.applied + b.applied,
    rejected: a.rejected + b.rejected,
    failed: a.failed + b.failed,
    authFailed: b.authFailed,
    networkFailed: b.networkFailed,
  };
}
